import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card, CardBody, CardHeader, Col, Nav, NavItem, NavLink,
  Row, TabContent, TabPane,
} from 'reactstrap';
import CommonCardHeader from '../../../../../coreComponents/CommonCardHeader';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../../reduxToolkit/hooks';
import { useGetStudentScoreByIdQuery } from '../../../../../reduxToolkit/reducers/usersSlice';
import { ScoreScrollTable } from '../../../../../data/formsAndTable/tables/scoreScrollTable/ScoreScrollTable';
import { SolidCircleChart } from '../../../../charts/SolidCircleChart';
import { useStudyProgressChartsOptions } from './useStudyProgressChartsOptions';
import { UserRole } from '../../../../../types/Forms';
import { getStudentCourses } from '../../../../../services/api';
import { ICourse } from '../../../../../types/Courses';
import { CustomLoader } from '../../../../uiKits/customLoader/CustomLoader';

const ProfileStudyProgress = () => {
  const { t } = useTranslation();
  const { id: studentId, role } = useAppSelector((state) => state.auth);

  const [coursesTabs, setCoursesTabs] = useState<ICourse[]>([]);
  const [activeCourseTab, setActiveCourseTab] = useState<ICourse | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Стан для графіків
  const [currentScore, setCurrentScore] = useState(0);
  const [numCurrentExamsPassed, setNumCurrentExamsPassed] = useState(0);
  const [currentExamsPercentPassed, setCurrentExamsPercentPassed] = useState(0);
  const [maxExams, setMaxExams] = useState(0);

  // Реф для захисту від race conditions
  const activeCourseIdRef = useRef(activeCourseTab?.id);

  // Оновлюємо реф при зміні activeCourseTab
  useEffect(() => {
    activeCourseIdRef.current = activeCourseTab?.id;
  }, [activeCourseTab]);

  // Завантаження курсів один раз при монтуванні
  useEffect(() => {
    const fetchCourses = async () => {
      const courses = await getStudentCourses(studentId);
      const assignedCourses = courses.filter((course: ICourse) => course.userCourseStatus !== null);
      setCoursesTabs(assignedCourses);
      if (assignedCourses.length > 0) {
        setActiveCourseTab(assignedCourses[0]);
      }
    };
    if (role === UserRole.STUDENT) {
      fetchCourses();
    }
  }, [studentId, role]);

  // Запит даних про успішність для активного курсу
  const {
    data: studentScore,
    isLoading: isScoreLoading,
    isFetching: isScoreFetching,
  } = useGetStudentScoreByIdQuery(
    { studentId, courseId: activeCourseTab?.id },
    {
      skip: role !== UserRole.STUDENT || !activeCourseTab?.id,
    },
  );

  // Обробка отриманих даних з перевіркою актуальності
  useEffect(() => {
    // Переконуємося, що дані відповідають поточному активному курсу
    if (activeCourseIdRef.current !== activeCourseTab?.id) return;

    if (!studentScore) {
      setCurrentExamsPercentPassed(0);
      setCurrentScore(0);
      setNumCurrentExamsPassed(0);
      setMaxExams(0);
      return;
    }

    console.log('Fetched student score:', studentScore);
    const scoresData = studentScore.scores?.data;

    if (scoresData && scoresData.length > 0) {
      const examPassPercentage =
        scoresData[0].maxExamsQuantity > 0
          ? (scoresData[0].totalLessonsWithPassedExams / scoresData[0].maxExamsQuantity) * 100
          : 0;

      setCurrentExamsPercentPassed(Number(examPassPercentage.toFixed(0)));
      setCurrentScore(scoresData[0].avgBestScore);
      setNumCurrentExamsPassed(scoresData[0].totalLessonsWithPassedExams);
      setMaxExams(scoresData[0].maxExamsQuantity);
    } else {
      // Дані є, але порожні – обнуляємо
      setCurrentExamsPercentPassed(0);
      setCurrentScore(0);
      setNumCurrentExamsPassed(0);
      setMaxExams(0);
    }
  }, [studentScore, activeCourseTab]);

  const handleTabChange = useCallback((course: ICourse) => {
    setActiveCourseTab(course);
    localStorage.setItem('stored-profile-tabs', `${course.id}`);
  }, []);

  const { examsPassedOptions, avgScoreOptions } = useStudyProgressChartsOptions(
    currentExamsPercentPassed,
    numCurrentExamsPassed,
    maxExams,
    currentScore,
  );

  return (
    <Col xl="8">
      <Card>
        <CardHeader className="pb-0">
          <CommonCardHeader title={t('profilePage.progressTitle')} headClass={'p-0'} />
        </CardHeader>
        {role === UserRole.STUDENT && (
          <CardBody >
            <Nav tabs className="simple-wrapper">
              {coursesTabs.map((item) => (
                <NavItem key={item.id}>
                  <NavLink
                    tag="button"
                    type="button"
                    className={`txt-info !h4 f-w-500 ${activeCourseTab?.id === item.id ? 'active' : ''}`}
                    onClick={() => handleTabChange(item)}
                  >
                    {item.name}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={String(activeCourseTab?.id)}>
              {coursesTabs.length === 0 && <CustomLoader />}
              {coursesTabs.map((item) => (
                <TabPane
                  key={item.id}
                  tabId={String(item.id)}
                  className={`fade ${activeCourseTab?.id === item.id ? 'show' : ''}`}
                >
                  <Row className="d-flex justify-content-center">
                    <Row>
                      <Col xs="6">
                        <SolidCircleChart
                           key={`exams-${item.id}-${currentExamsPercentPassed}-${maxExams}`}
                          activity={examsPassedOptions}
                        />
                      </Col>
                      <Col xs="6">
                        <SolidCircleChart
                         key={`score-${item.id}-${currentScore}`}
                          activity={avgScoreOptions}
                        />
                      </Col>
                    </Row>
                    <Col xs="12">
                      <ScoreScrollTable
                        key={`table-${item.id}`}
                        rows={studentScore?.lessons?.rows || []}
                        page={page}
                        perPage={perPage}
                        setPage={setPage}
                        setPerPage={setPerPage}
                        hasFilter={false}
                        isScoreBoardLoading={isScoreLoading}
                        isScoreBoardFetching={isScoreFetching}
                        scoreBoard={studentScore?.lessons}
                        showUsernames={false}
                      />
                    </Col>
                  </Row>
                </TabPane>
              ))}
            </TabContent>
          </CardBody>
        )}
        {role !== UserRole.STUDENT && (
          <CardBody>
            <div className="text-muted text-center">{t('profilePage.noAccessToProgress')}</div>
          </CardBody>
        )}
      </Card>
    </Col>
  );
};

export default ProfileStudyProgress;